/**
 * ReportsTab — CON-04/05: Reportes Contables (Diario, Mayor, Balance, Análisis, Balance 8 Col.,
 *              Balance Clasificado, Estado de Resultados)
 */
import { todayISO } from '../../../lib/utils'
import { useState, useMemo } from 'react'
import DatePicker from '@/components/DatePicker'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { VoucherViewerModal } from './VoucherViewerModal'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { fmtDate, formatRut } from '@/lib/utils'
import { useCompanyProfile } from '@/modules/config/hooks/useConfig'
import { addPdfHeader, addPdfFooter, loadLogoBase64, type PdfCompanyInfo } from '@/lib/pdfUtils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { BarChart3, Download, FileText, Loader2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useJournalReport, useLedgerReport, useTrialBalanceReport,
  useAccountAnalysis, useEightColumnBalance,
  useClassifiedBalance, useIncomeStatement, useCashFlowStatement,
  useAccounts, useCostCenters,
  type AccountType, type EightColumnBalanceRow, type EightColumnBalanceResponse,
  type ClassifiedBalanceResponse, type IncomeStatementResponse,
  type CashFlowStatementResponse,
  type JournalResponse,
  type LedgerResponse, type LedgerAllResponse, type LedgerAccountData,
} from '../hooks/useAccounting'
import { EmptyState } from '@/components/EmptyState'

type ReportType = 'journal' | 'ledger' | 'trial-balance' | 'analysis' | 'eight-column-balance' | 'classified-balance' | 'income-statement' | 'cash-flow'

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset:     'Activo',
  liability: 'Pasivo',
  equity:    'Patrimonio',
  revenue:   'Ingresos',
  expense:   'Gastos',
  other:     'Otro',
}

/**
 * Formatea un monto de los reportes contables.
 *
 * ⚠️ Tenía `es-CL` + `CLP` fijos, así que un tenant peruano veía su balance y su
 * EERR en soles con el signo del peso chileno y sin céntimos. Estos reportes son
 * **genéricos** (Diario, Mayor, Balance, EERR, Flujo) — a diferencia de las DJ del
 * SII, donde el CLP fijo SÍ es correcto porque son declaraciones chilenas que se
 * presentan en pesos. No «arreglar» esas.
 */
function fmtCLP(value: string | undefined) {
  if (!value || value === '0.00' || value === '0') return '—'
  const n = parseFloat(value)
  if (isNaN(n) || n === 0) return '—'
  return formatMoneyForTenant(n)
}

function fmtNum(value: string | undefined) {
  if (!value) return '—'
  const n = parseFloat(value)
  if (isNaN(n)) return value
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
}

function isSingleLedger(data: LedgerResponse | LedgerAllResponse): data is LedgerResponse {
  return 'movements' in data
}

// ─── Excel Export helpers ────────────────────────────────────────────────────

/** Prepends 6 company info rows (legal_name, RUT, giro, blank, title, period) to a worksheet. */
function addCompanyHeaderToSheet(
  ws: XLSX.WorkSheet,
  company: PdfCompanyInfo,
  title: string,
  period: string,
): void {
  const headerAoa = [
    [company.legal_name || ''],
    [company.tax_id ? `RUT: ${company.tax_id}` : ''],
    [company.business_activity || ''],
    [title],
    [period],
    [],
  ]
  // Shift existing range down by 6 rows
  if (ws['!ref']) {
    const range = XLSX.utils.decode_range(ws['!ref'])
    range.e.r += headerAoa.length
    ws['!ref'] = XLSX.utils.encode_range(range)
  }
  XLSX.utils.sheet_add_aoa(ws, headerAoa, { origin: 'A1' })
}

function exportJournalExcel(data: { date_from: string; date_to: string; entries: Array<{
  number: string; date: string; description: string; total_debe: string; total_haber: string;
  lines: Array<{ account_code: string; account_name: string; glosa: string; debe: string; haber: string }>
}> }, company: PdfCompanyInfo) {
  const rows: Record<string, string | number>[] = []
  for (const entry of data.entries) {
    for (const line of entry.lines) {
      rows.push({
        'Comprobante': entry.number,
        'Fecha': entry.date,
        'Descripción': entry.description,
        'Cuenta': line.account_code,
        'Nombre Cuenta': line.account_name,
        'Glosa': line.glosa,
        'Debe': parseFloat(line.debe) || 0,
        'Haber': parseFloat(line.haber) || 0,
      })
    }
  }
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Libro Diario', `${data.date_from} al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Libro Diario')
  XLSX.writeFile(wb, `libro_diario_${data.date_from}_${data.date_to}.xlsx`)
}

function exportJournalPDF(
  data: JournalResponse,
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF()
  let y = addPdfHeader(
    doc, company,
    'Libro Diario',
    `${data.date_from} al ${data.date_to}`,
    logoBase64,
  )

  const getLastY = (): number =>
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  for (const entry of data.entries) {
    if (y > 250) { doc.addPage(); y = 15 }

    // Entry header bar
    const pageW = doc.internal.pageSize.getWidth()
    doc.setFillColor(240, 243, 255)
    doc.rect(14, y - 4, pageW - 28, 6, 'F')
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(58, 98, 216)
    doc.text(`${entry.number}`, 16, y)
    doc.setTextColor(60, 60, 80)
    doc.text(`${entry.date}  —  ${entry.description}`, 40, y)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(30, 40, 60)
    y += 3

    const body: string[][] = entry.lines.map(l => [
      l.account_code,
      l.account_name,
      l.glosa || '',
      parseFloat(l.debe) > 0 ? fmtNum(l.debe) : '',
      parseFloat(l.haber) > 0 ? fmtNum(l.haber) : '',
    ])

    autoTable(doc, {
      startY: y,
      body,
      theme: 'plain',
      styles: { fontSize: 7.5, cellPadding: 1.2 },
      columnStyles: {
        0: { cellWidth: 18, font: 'courier', textColor: [100, 100, 100] as [number, number, number] },
        1: { cellWidth: 72 },
        2: { cellWidth: 60, textColor: [120, 120, 120] as [number, number, number] },
        3: { cellWidth: 24, halign: 'right' },
        4: { cellWidth: 24, halign: 'right', textColor: [100, 100, 100] as [number, number, number] },
      },
      margin: { left: 14, right: 14, bottom: 32 },
    })

    y = getLastY() + 5

    // Thin separator between entries
    doc.setDrawColor(210, 215, 225)
    doc.setLineWidth(0.2)
    doc.line(14, y - 2, doc.internal.pageSize.getWidth() - 14, y - 2)
  }

  addPdfFooter(doc)
  doc.save(`libro_diario_${data.date_from}_${data.date_to}.pdf`)
}

function exportLedgerExcel(
  dateFrom: string, dateTo: string,
  ledgers: LedgerAccountData[],
  company: PdfCompanyInfo,
) {
  const rows: Record<string, string | number>[] = []
  for (const acct of ledgers) {
    for (const m of acct.movements) {
      rows.push({
        'Cuenta': acct.account_code,
        'Nombre Cuenta': acct.account_name,
        'Fecha': m.date,
        'Comprobante': m.voucher_number,
        'Descripción': m.voucher_description,
        'Glosa': m.glosa,
        'Debe': parseFloat(m.debe) || 0,
        'Haber': parseFloat(m.haber) || 0,
        'Saldo': parseFloat(m.balance) || 0,
      })
    }
    // Subtotal row per account
    rows.push({
      'Cuenta': acct.account_code,
      'Nombre Cuenta': `TOTAL ${acct.account_name}`,
      'Fecha': '', 'Comprobante': '', 'Descripción': '', 'Glosa': '',
      'Debe': parseFloat(acct.total_debe) || 0,
      'Haber': parseFloat(acct.total_haber) || 0,
      'Saldo': '',
    } as Record<string, string | number>)
  }
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Libro Mayor', `${dateFrom} al ${dateTo}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Libro Mayor')
  XLSX.writeFile(wb, `libro_mayor_${dateFrom}_${dateTo}.xlsx`)
}

function exportLedgerPDF(
  dateFrom: string,
  dateTo: string,
  ledgers: LedgerAccountData[],
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF()
  let y = addPdfHeader(
    doc, company,
    'Libro Mayor',
    `${dateFrom} al ${dateTo}`,
    logoBase64,
  )

  const getLastY = (): number =>
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  for (const acct of ledgers) {
    if (y > 240) { doc.addPage(); y = 15 }

    // Account section header in blue
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(58, 98, 216)
    doc.text(`${acct.account_code}  ${acct.account_name}`, 14, y)
    doc.setTextColor(30, 40, 60)
    y += 2

    const body: string[][] = acct.movements.map(m => [
      m.date,
      m.voucher_number,
      (m.voucher_description || '').slice(0, 30),
      m.glosa || '',
      parseFloat(m.debe) > 0 ? fmtNum(m.debe) : '',
      parseFloat(m.haber) > 0 ? fmtNum(m.haber) : '',
      fmtNum(m.balance),
    ])
    body.push(['', 'TOTALES', '', '', fmtNum(acct.total_debe), fmtNum(acct.total_haber), ''])

    autoTable(doc, {
      startY: y,
      head: [['Fecha', 'Comprob.', 'Descripción', 'Glosa', 'Debe', 'Haber', 'Saldo']],
      body,
      theme: 'plain',
      styles: { fontSize: 7.5, cellPadding: 1.2 },
      headStyles: {
        fillColor: [240, 243, 255] as [number, number, number],
        textColor: [58, 98, 216] as [number, number, number],
        fontStyle: 'bold',
        fontSize: 7,
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 22, font: 'courier' },
        2: { cellWidth: 40 },
        3: { cellWidth: 38 },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 24, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 14, right: 14, bottom: 32 },
      didParseCell: (hookData) => {
        if (hookData.row.index === acct.movements.length && hookData.section === 'body') {
          hookData.cell.styles.fontStyle = 'bold'
          hookData.cell.styles.fillColor = [240, 243, 255]
        }
      },
    })

    y = getLastY() + 7
  }

  addPdfFooter(doc)
  doc.save(`libro_mayor_${dateFrom}_${dateTo}.pdf`)
}

function exportTrialBalanceExcel(data: {
  date_from: string; date_to: string;
  accounts: Array<{
    account_code: string; account_name: string; account_type: string;
    opening_debe: string; opening_haber: string;
    period_debe: string; period_haber: string;
    final_debe: string; final_haber: string;
  }>
}, company: PdfCompanyInfo) {
  const rows = data.accounts.map(r => ({
    'Código': r.account_code,
    'Cuenta': r.account_name,
    'Tipo': ACCOUNT_TYPE_LABELS[r.account_type as AccountType] || r.account_type,
    'Saldo Ini. Debe': parseFloat(r.opening_debe) || 0,
    'Saldo Ini. Haber': parseFloat(r.opening_haber) || 0,
    'Mov. Debe': parseFloat(r.period_debe) || 0,
    'Mov. Haber': parseFloat(r.period_haber) || 0,
    'Saldo Final Debe': parseFloat(r.final_debe) || 0,
    'Saldo Final Haber': parseFloat(r.final_haber) || 0,
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Balance de Comprobación', `${data.date_from} al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Balance Comprobación')
  XLSX.writeFile(wb, `balance_comprobacion_${data.date_from}_${data.date_to}.xlsx`)
}

function exportAnalysisExcel(data: {
  account_code: string; account_name: string; date_from: string; date_to: string;
  movements: Array<{
    date: string; voucher_number: string; glosa: string;
    cost_center_code: string | null; cost_center_name: string | null;
    debe: string; haber: string; running_balance: string;
  }>
}, company: PdfCompanyInfo) {
  const rows = data.movements.map(m => ({
    'Fecha': m.date,
    'Comprobante': m.voucher_number,
    'Glosa': m.glosa,
    'Centro de Costo': m.cost_center_code ? `${m.cost_center_code} ${m.cost_center_name}` : '',
    'Debe': parseFloat(m.debe) || 0,
    'Haber': parseFloat(m.haber) || 0,
    'Saldo': parseFloat(m.running_balance) || 0,
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, `Análisis — ${data.account_code} ${data.account_name}`, `${data.date_from} al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, `${data.account_code} ${data.account_name}`.slice(0, 31))
  XLSX.writeFile(wb, `analisis_${data.account_code}_${data.date_from}_${data.date_to}.xlsx`)
}

function ExportButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-success-fill text-white text-xs font-medium rounded-lg hover:bg-success-fill-hover disabled:opacity-40">
      <Download size={13} /> Exportar Excel
    </button>
  )
}

function PDFButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-danger-fill text-white text-xs font-medium rounded-lg hover:bg-danger-fill-hover disabled:opacity-40">
      <FileText size={13} /> Exportar PDF
    </button>
  )
}

// ─── Excel + PDF exports for Balance Clasificado ─────────────────────────────

function exportClassifiedBalanceExcel(data: ClassifiedBalanceResponse, company: PdfCompanyInfo) {
  const rows: Record<string, string | number>[] = []
  const addSection = (label: string, accounts: { account_code: string; account_name: string; balance: string }[], total: string) => {
    rows.push({ 'Sección': label, 'Código': '', 'Cuenta': '', 'Saldo': '' })
    for (const a of accounts) {
      rows.push({ 'Sección': '', 'Código': a.account_code, 'Cuenta': a.account_name, 'Saldo': parseFloat(a.balance) || 0 })
    }
    rows.push({ 'Sección': '', 'Código': '', 'Cuenta': `TOTAL ${label}`, 'Saldo': parseFloat(total) || 0 })
    rows.push({ 'Sección': '', 'Código': '', 'Cuenta': '', 'Saldo': '' })
  }
  addSection('Activos Corrientes', data.current_assets.accounts, data.current_assets.total)
  addSection('Activos No Corrientes', data.non_current_assets.accounts, data.non_current_assets.total)
  rows.push({ 'Sección': '', 'Código': '', 'Cuenta': 'TOTAL ACTIVOS', 'Saldo': parseFloat(data.total_assets) || 0 })
  rows.push({ 'Sección': '', 'Código': '', 'Cuenta': '', 'Saldo': '' })
  addSection('Pasivos Corrientes', data.current_liabilities.accounts, data.current_liabilities.total)
  addSection('Pasivos No Corrientes', data.non_current_liabilities.accounts, data.non_current_liabilities.total)
  addSection('Patrimonio', data.equity.accounts, data.equity.total)
  rows.push({ 'Sección': '', 'Código': '', 'Cuenta': 'TOTAL PATRIMONIO Y PASIVOS', 'Saldo': parseFloat(data.total_liabilities_equity) || 0 })
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Balance Clasificado', `Al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Balance Clasificado')
  XLSX.writeFile(wb, `balance_clasificado_${data.date_to}.xlsx`)
}

function exportClassifiedBalancePDF(
  data: ClassifiedBalanceResponse,
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF()
  const startY = addPdfHeader(
    doc, company,
    'Balance Clasificado',
    `Al ${data.date_to}`,
    logoBase64,
  )

  const PAGE_W = doc.internal.pageSize.getWidth()
  const L_MARGIN = 14
  const R_MARGIN = 14
  const MID = PAGE_W / 2
  // Left col: L_MARGIN to MID-3, Right col: MID+3 to PAGE_W-R_MARGIN
  const LEFT_RIGHT_MARGIN = PAGE_W - MID + 3
  const RIGHT_LEFT_MARGIN = MID + 3

  let leftY = startY
  let rightY = startY

  // Track the page where content starts to reset before drawing right column
  const contentStartPage = (doc.internal as unknown as { getCurrentPageInfo: () => { pageNumber: number } }).getCurrentPageInfo().pageNumber

  const getLastY = (): number =>
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  type AcctRow = { account_code: string; account_name: string; balance: string }

  const addSection = (
    side: 'left' | 'right',
    title: string,
    accounts: AcctRow[],
    total: string,
  ) => {
    const isLeft = side === 'left'
    const mLeft = isLeft ? L_MARGIN : RIGHT_LEFT_MARGIN
    const mRight = isLeft ? LEFT_RIGHT_MARGIN : R_MARGIN
    const y = isLeft ? leftY : rightY

    // Section title in blue
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(58, 98, 216)
    doc.text(title.toUpperCase(), mLeft, y)
    const tableY = y + 2

    const body: string[][] = accounts.map(a => [a.account_code, a.account_name, fmtNum(a.balance)])
    body.push(['', `Total ${title}`, fmtNum(total)])

    autoTable(doc, {
      startY: tableY,
      head: [['Cód.', 'Cuenta', 'Saldo']],
      body,
      theme: 'plain',
      styles: { fontSize: 7, cellPadding: 1.2 },
      headStyles: {
        fillColor: [240, 243, 255] as [number, number, number],
        textColor: [58, 98, 216] as [number, number, number],
        fontStyle: 'bold',
        fontSize: 6.5,
      },
      columnStyles: { 0: { cellWidth: 16, font: 'courier' }, 2: { halign: 'right', cellWidth: 22 } },
      margin: { left: mLeft, right: mRight, bottom: 32 },
    })

    const finalY = getLastY() + 3
    if (isLeft) leftY = finalY
    else rightY = finalY
    doc.setTextColor(30, 40, 60)
  }

  // Column headers
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 40, 60)
  doc.text('ACTIVOS', L_MARGIN + 20, leftY - 1)
  doc.text('PASIVOS Y PATRIMONIO', MID + 8, rightY - 1)
  leftY += 4
  rightY += 4

  // Left: Assets
  addSection('left', 'Activos Corrientes', data.current_assets.accounts, data.current_assets.total)
  addSection('left', 'Activos No Corrientes', data.non_current_assets.accounts, data.non_current_assets.total)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 40, 60)
  doc.text('TOTAL ACTIVOS', L_MARGIN, leftY)
  doc.text(fmtNum(data.total_assets), MID - L_MARGIN, leftY, { align: 'right' })
  leftY += 6

  // Reset to content start page before drawing right column so both columns
  // are positioned correctly side-by-side on the same page(s).
  doc.setPage(contentStartPage)
  rightY = startY + 4  // same starting Y as left column (after headers increment)

  // Right: Liabilities + Equity
  addSection('right', 'Pasivos Corrientes', data.current_liabilities.accounts, data.current_liabilities.total)
  addSection('right', 'Pasivos No Corrientes', data.non_current_liabilities.accounts, data.non_current_liabilities.total)

  const eqAccounts: AcctRow[] = [
    ...data.equity.accounts,
    { account_code: '', account_name: 'Ganancia (pérdida) del ejercicio', balance: data.equity.result_current_year },
  ]
  addSection('right', 'Patrimonio', eqAccounts, data.equity.total)

  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 40, 60)
  doc.text('TOTAL PATRIMONIO Y PASIVOS', MID + 3, rightY)
  doc.text(fmtNum(data.total_liabilities_equity), PAGE_W - R_MARGIN, rightY, { align: 'right' })

  addPdfFooter(doc)
  doc.save(`balance_clasificado_${data.date_to}.pdf`)
}

// ─── Excel + PDF exports for Estado de Resultados ────────────────────────────

function exportIncomeStatementExcel(data: IncomeStatementResponse, company: PdfCompanyInfo) {
  const rows = data.line_items.map(li => ({
    'Concepto': li.is_subtotal ? li.label.toUpperCase() : `${'  '.repeat(li.indent)}${li.label}`,
    'Monto': parseFloat(li.amount) || 0,
  }))
  rows.push({ 'Concepto': '', 'Monto': '' as unknown as number })
  rows.push({ 'Concepto': 'RESULTADO NETO', 'Monto': parseFloat(data.net_result) || 0 })
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Estado de Resultados', `${data.date_from} al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Estado de Resultados')
  XLSX.writeFile(wb, `estado_resultados_${data.date_from}_${data.date_to}.xlsx`)
}

function exportIncomeStatementPDF(
  data: IncomeStatementResponse,
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF()
  const startY = addPdfHeader(
    doc, company,
    'Estado de Resultados por Función',
    `${data.date_from} al ${data.date_to}`,
    logoBase64,
  )

  const body = data.line_items.map(li => {
    const label = li.is_subtotal ? li.label : `${'   '.repeat(li.indent)}${li.label}`
    const n = parseFloat(li.amount)
    const formatted = n < 0 ? `(${fmtNum(String(Math.abs(n)))})` : fmtNum(li.amount)
    return [label, formatted]
  })
  const netN = parseFloat(data.net_result)
  const netFormatted = netN < 0 ? `(${fmtNum(String(Math.abs(netN)))})` : fmtNum(data.net_result)
  body.push(['', ''])
  body.push(['RESULTADO NETO', netFormatted])

  autoTable(doc, {
    startY,
    head: [['Concepto', 'Monto']],
    body,
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [58, 98, 216] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right', cellWidth: 40 } },
    margin: { left: 14, right: 14, bottom: 32 },
    didParseCell: (hookData) => {
      const row = data.line_items[hookData.row.index]
      if (row?.is_subtotal && hookData.section === 'body') {
        hookData.cell.styles.fontStyle = 'bold'
        hookData.cell.styles.fillColor = [240, 243, 255]
      }
      // Style RESULTADO NETO row (2 rows after line_items)
      if (hookData.row.index === data.line_items.length + 1 && hookData.section === 'body') {
        hookData.cell.styles.fontStyle = 'bold'
        hookData.cell.styles.fillColor = [58, 98, 216]
        hookData.cell.styles.textColor = [255, 255, 255]
      }
    },
  })

  addPdfFooter(doc)
  doc.save(`estado_resultados_${data.date_from}_${data.date_to}.pdf`)
}

// ─── Excel + PDF exports for Estado de Flujo de Efectivo ─────────────────────

interface CashFlowLine { label: string; amount: string; isSubtotal: boolean; indent: number }

function cashFlowLineItems(d: CashFlowStatementResponse): CashFlowLine[] {
  const items: CashFlowLine[] = []
  items.push({ label: 'Efectivo y equivalentes al inicio del período', amount: d.efectivo_inicial, isSubtotal: true, indent: 0 })
  for (const key of ['operacion', 'inversion', 'financiamiento', 'sin_clasificar'] as const) {
    const act = d.actividades[key]
    if (act.lineas.length === 0 && parseFloat(act.total) === 0) continue
    act.lineas.forEach(l => items.push({ label: `${l.account_code} ${l.label}`, amount: l.amount, isSubtotal: false, indent: 1 }))
    items.push({ label: act.label, amount: act.total, isSubtotal: true, indent: 0 })
  }
  items.push({ label: 'Variación neta del efectivo', amount: d.variacion_efectivo, isSubtotal: true, indent: 0 })
  items.push({ label: 'Efectivo y equivalentes al final del período', amount: d.efectivo_final, isSubtotal: true, indent: 0 })
  return items
}

function exportCashFlowExcel(data: CashFlowStatementResponse, company: PdfCompanyInfo) {
  const rows = cashFlowLineItems(data).map(li => ({
    'Concepto': li.isSubtotal ? li.label.toUpperCase() : `  ${li.label}`,
    'Monto': parseFloat(li.amount) || 0,
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  addCompanyHeaderToSheet(ws, company, 'Estado de Flujo de Efectivo', `${data.date_from} al ${data.date_to}`)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Flujo de Efectivo')
  XLSX.writeFile(wb, `flujo_efectivo_${data.date_from}_${data.date_to}.xlsx`)
}

function exportCashFlowPDF(
  data: CashFlowStatementResponse,
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF()
  const startY = addPdfHeader(
    doc, company,
    'Estado de Flujo de Efectivo (método directo)',
    `${data.date_from} al ${data.date_to}`,
    logoBase64,
  )
  const items = cashFlowLineItems(data)
  const body = items.map(li => {
    const label = li.isSubtotal ? li.label : `   ${li.label}`
    const n = parseFloat(li.amount)
    const formatted = n < 0 ? `(${fmtNum(String(Math.abs(n)))})` : fmtNum(li.amount)
    return [label, formatted]
  })
  autoTable(doc, {
    startY,
    head: [['Concepto', 'Monto']],
    body,
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [58, 98, 216] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right', cellWidth: 40 } },
    margin: { left: 14, right: 14, bottom: 32 },
    didParseCell: (hookData) => {
      const row = items[hookData.row.index]
      if (row?.isSubtotal && hookData.section === 'body') {
        hookData.cell.styles.fontStyle = 'bold'
        hookData.cell.styles.fillColor = [240, 243, 255]
      }
    },
  })
  addPdfFooter(doc)
  doc.save(`flujo_efectivo_${data.date_from}_${data.date_to}.pdf`)
}

// ─── PDF export for Balance 8 Columnas ───────────────────────────────────────

const COL8_KEYS = ['debitos', 'creditos', 'deudor', 'acreedor', 'activo', 'pasivo', 'perdida', 'ganancia'] as const
type Col8Key = typeof COL8_KEYS[number]

function exportEightColPDF(
  d: EightColumnBalanceResponse,
  company: PdfCompanyInfo,
  logoBase64: string | null,
) {
  const doc = new jsPDF({ orientation: 'landscape' })
  const startY = addPdfHeader(
    doc, company,
    'Balance de 8 Columnas',
    `${d.date_from} al ${d.date_to}`,
    logoBase64,
  )

  const body: string[][] = d.accounts.map(r => [
    r.account_code,
    r.account_name,
    ...COL8_KEYS.map(k => parseFloat(r[k as keyof EightColumnBalanceRow] as string) !== 0 ? fmtNum(r[k as keyof EightColumnBalanceRow] as string) : ''),
  ])

  if (d.totals) {
    body.push(['', 'TOTALES', ...COL8_KEYS.map(k => fmtNum(d.totals[k as Col8Key] ?? '0'))])
  }
  if (d.resultado_ejercicio) {
    const resEj = parseFloat(d.resultado_ejercicio)
    const isLoss = resEj < 0
    body.push([
      '', isLoss ? 'Resultado del Ejercicio (Pérdida)' : 'Resultado del Ejercicio (Ganancia)',
      '', '', '', '', '', '',
      isLoss ? fmtNum(String(Math.abs(resEj))) : '',  // col Pérdida
      !isLoss ? fmtNum(d.resultado_ejercicio) : '',   // col Ganancia
    ])
  }

  autoTable(doc, {
    startY,
    head: [['Cuenta', 'Nombre', 'Débitos', 'Créditos', 'Deudor', 'Acreedor', 'Activo', 'Pasivo', 'Pérdida', 'Ganancia']],
    body,
    theme: 'striped',
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: {
      fillColor: [58, 98, 216] as [number, number, number],
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold',
    },
    columnStyles: {
      0: { cellWidth: 18, font: 'courier' },
      1: { cellWidth: 55 },
      2: { halign: 'right', cellWidth: 24 },
      3: { halign: 'right', cellWidth: 24 },
      4: { halign: 'right', cellWidth: 24 },
      5: { halign: 'right', cellWidth: 24 },
      6: { halign: 'right', cellWidth: 24 },
      7: { halign: 'right', cellWidth: 24 },
      8: { halign: 'right', cellWidth: 24 },
      9: { halign: 'right', cellWidth: 24 },
    },
    margin: { left: 10, right: 10, bottom: 32 },
    didParseCell: (hookData) => {
      const isTotal = hookData.row.index >= d.accounts.length
      if (isTotal && hookData.section === 'body') {
        hookData.cell.styles.fontStyle = 'bold'
        hookData.cell.styles.fillColor = [240, 243, 255]
      }
    },
  })

  addPdfFooter(doc)
  doc.save(`balance_8_columnas_${d.date_from}_${d.date_to}.pdf`)
}

// ─── Single account ledger table ─────────────────────────────────────────────

function LedgerTable({ data, onVoucherClick }: { data: LedgerAccountData; onVoucherClick?: (voucherId?: string) => void }) {
  const locale = useTenantLocale()
  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden mb-4 shadow-card">
      <div className="px-4 py-2.5 bg-row-alt/50 border-b border-divider flex items-center justify-between">
        <span className="text-sm font-semibold text-content">
          <span className="font-mono mr-2">{data.account_code}</span>{data.account_name}
        </span>
        <span className="text-xs text-content-subtle">
          Saldo anterior <span className="tabular-nums font-medium text-content">{fmtNum(data.opening_balance ?? '0')}</span>
          {' · '}{data.count} movimiento{data.count !== 1 ? 's' : ''}
        </span>
      </div>
      <DataTable
        columns={[
          { key: 'date', header: 'Fecha', render: m => fmtDate(m.date, locale) },
          {
            key: 'voucher_number', header: 'Comprobante',
            render: m => (
              <span className={`font-mono ${onVoucherClick ? 'text-primary underline decoration-dotted' : 'text-content'}`}>
                {m.voucher_number}
              </span>
            ),
          },
          {
            key: 'voucher_description', header: 'Descripción',
            render: m => <span className="block max-w-[160px] truncate">{m.voucher_description}</span>,
          },
          { key: 'glosa', header: 'Glosa' },
          { key: 'debe', header: 'Debe', numeric: true, sortValue: m => parseFloat(m.debe), render: m => (parseFloat(m.debe) > 0 ? fmtNum(m.debe) : '—') },
          { key: 'haber', header: 'Haber', numeric: true, sortValue: m => parseFloat(m.haber), render: m => (parseFloat(m.haber) > 0 ? fmtNum(m.haber) : '—') },
          {
            key: 'balance', header: 'Saldo', numeric: true,
            sortValue: m => parseFloat(m.balance),
            render: m => <span className="font-semibold text-content">{fmtNum(m.balance)}</span>,
          },
        ]}
        rows={data.movements}
        rowKey={(_, i) => i}
        onRowClick={onVoucherClick ? m => onVoucherClick(m.voucher_id) : undefined}
        footer={{ debe: fmtNum(data.total_debe), haber: fmtNum(data.total_haber), balance: fmtNum(data.closing_balance ?? '0') }}
        footerLabel="TOTALES"
        surface={false}
        minWidth={900}
        caption="Movimientos de la cuenta"
      />
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────


/**
 * Rótulo de los asientos de cierre y apertura en el libro diario. El diario los
 * muestra todos; los demás reportes no (backend `accounting/exclusiones.py`), y
 * sin el rótulo el contador ve un asiento en el diario que el mayor no suma.
 */
const TIPO_DE_ASIENTO = {
  reopening: {
    rotulo: 'Apertura repetida',
    ayuda: 'Repite saldos que ya están en las cuentas: no suma en el mayor, los balances ni el flujo de efectivo.',
  },
  closing: {
    rotulo: 'Cierre',
    ayuda: 'Salda los resultados contra patrimonio: cuenta en los saldos, no en el estado de resultados.',
  },
  genesis: {
    rotulo: 'Saldos iniciales',
    ayuda: 'Saldos con que arrancan los libros: cuentan una vez y no son flujo de efectivo.',
  },
} as const

export function ReportsTab() {
  const locale = useTenantLocale()
  const today = todayISO()
  const firstOfMonth = today.substring(0, 7) + '-01'

  const [reportType, setReportType] = useState<ReportType>('journal')
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [selectedCostCenterId, setSelectedCostCenterId] = useState('')
  const [generated, setGenerated] = useState(false)
  const [hideZeroAccounts, setHideZeroAccounts] = useState(false)
  const [viewVoucherId, setViewVoucherId] = useState<string | null>(null)
  const [drillDownFrom, setDrillDownFrom] = useState<ReportType | null>(null)

  const openVoucher = (voucherId?: string) => {
    if (voucherId) setViewVoucherId(voucherId)
  }

  const drillDownToAccount = (accountId: string) => {
    setDrillDownFrom(reportType)
    setSelectedAccountId(accountId)
    setReportType('analysis')
    setGenerated(true)
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const backToPreviousReport = () => {
    if (!drillDownFrom) return
    const prev = drillDownFrom
    setDrillDownFrom(null)
    setSelectedAccountId('')
    setReportType(prev)
    setGenerated(true)
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const accounts = accountsData?.accounts ?? []

  const { data: costCentersData } = useCostCenters({ is_active: 'true' })
  const costCenters = costCentersData?.cost_centers ?? []

  const accountItems = useMemo<AutocompleteItem[]>(
    () => accounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` })),
    [accounts],
  )
  const costCenterItems = useMemo<AutocompleteItem[]>(
    () => costCenters.map(cc => ({ id: cc.id, label: `${cc.code} — ${cc.name}` })),
    [costCenters],
  )
  const selectedAccountLabel = accountItems.find(i => i.id === selectedAccountId)?.label ?? ''
  const selectedCostCenterLabel = costCenterItems.find(i => i.id === selectedCostCenterId)?.label ?? ''

  const accountByCode = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of accounts) map.set(a.code, a.id)
    return map
  }, [accounts])
  const drillDownByCode = (code?: string) => {
    if (!code) return
    const id = accountByCode.get(code)
    if (id) drillDownToAccount(id)
  }

  const journalParams = generated && reportType === 'journal' && dateFrom && dateTo
    ? { date_from: dateFrom, date_to: dateTo }
    : null
  const ledgerParams = generated && reportType === 'ledger' && dateFrom && dateTo
    ? { date_from: dateFrom, date_to: dateTo, ...(selectedAccountId ? { account_id: selectedAccountId } : {}) }
    : null
  const trialParams = generated && reportType === 'trial-balance' && dateFrom && dateTo
    ? { date_from: dateFrom, date_to: dateTo }
    : null
  const analysisParams = generated && reportType === 'analysis' && dateFrom && dateTo && selectedAccountId
    ? {
        account_id: selectedAccountId,
        date_from: dateFrom,
        date_to: dateTo,
        ...(selectedCostCenterId ? { cost_center_id: selectedCostCenterId } : {}),
      }
    : null
  const eightColParams = generated && reportType === 'eight-column-balance' && dateFrom && dateTo
    ? { date_from: dateFrom, date_to: dateTo }
    : null
  const classifiedBalanceParams = generated && reportType === 'classified-balance' && dateTo
    ? { date_to: dateTo }
    : null
  const incomeStatementParams = generated && reportType === 'income-statement' && dateFrom && dateTo
    ? {
        date_from: dateFrom, date_to: dateTo,
        ...(selectedCostCenterId ? { cost_center_id: selectedCostCenterId } : {}),
      }
    : null
  const cashFlowParams = generated && reportType === 'cash-flow' && dateFrom && dateTo
    ? { date_from: dateFrom, date_to: dateTo }
    : null

  const journal = useJournalReport(journalParams)
  const ledger = useLedgerReport(ledgerParams)
  const trialBalance = useTrialBalanceReport(trialParams)
  const analysis = useAccountAnalysis(analysisParams)
  const eightCol = useEightColumnBalance(eightColParams)
  const classifiedBalance = useClassifiedBalance(classifiedBalanceParams)
  const incomeStatement = useIncomeStatement(incomeStatementParams)
  const cashFlow = useCashFlowStatement(cashFlowParams)

  const isLoading = journal.isLoading || ledger.isLoading || trialBalance.isLoading || analysis.isLoading || eightCol.isLoading || classifiedBalance.isLoading || incomeStatement.isLoading || cashFlow.isLoading

  const { data: companyProfile } = useCompanyProfile()

  const getPdfCompany = (): PdfCompanyInfo => ({
    legal_name: companyProfile?.legal_name ?? '',
    tax_id: companyProfile?.tax_id ? formatRut(companyProfile.tax_id) : '',
    business_activity: companyProfile?.business_activity ?? '',
    address_street: companyProfile?.address_street ?? '',
    address_city: companyProfile?.address_city ?? '',
    logo_url: companyProfile?.logo_url ?? null,
  })

  /** Fetches logo as base64 then calls fn(logoBase64). */
  const withLogo = async (fn: (logoBase64: string | null) => void) => {
    const logoUrl = companyProfile?.logo_url
    const logoBase64 = logoUrl ? await loadLogoBase64(logoUrl) : null
    fn(logoBase64)
  }

  const handleGenerate = () => setGenerated(true)

  // Account selector: required for analysis, optional for ledger, hidden for others
  const showAccountSelector = reportType === 'ledger' || reportType === 'analysis'
  const accountRequired = reportType === 'analysis'

  return (
    <div>
      {/* Filter form */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 mb-6 shadow-card">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="reportstab-tipo-de-reporte" className="block text-xs font-medium text-content-muted mb-1">Tipo de Reporte</label>
            <select id="reportstab-tipo-de-reporte"
              value={reportType}
              onChange={e => { setReportType(e.target.value as ReportType); setGenerated(false); setDrillDownFrom(null) }}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="journal">Libro Diario</option>
              <option value="ledger">Libro Mayor</option>
              <option value="trial-balance">Balance de Comprobación</option>
              <option value="analysis">Análisis de Cuenta</option>
              <option value="eight-column-balance">Balance de 8 Columnas</option>
              <option value="classified-balance">Balance Clasificado</option>
              <option value="income-statement">Estado de Resultados</option>
              <option value="cash-flow">Estado de Flujo de Efectivo</option>
            </select>
          </div>
          <div>
            <label htmlFor="reportstab-fecha-desde" className="block text-xs font-medium text-content-muted mb-1">Fecha Desde *</label>
            <DatePicker id="reportstab-fecha-desde" value={dateFrom} onChange={v => { setDateFrom(v); setGenerated(false) }} />
          </div>
          <div>
            <label htmlFor="reportstab-fecha-hasta" className="block text-xs font-medium text-content-muted mb-1">Fecha Hasta *</label>
            <DatePicker id="reportstab-fecha-hasta" value={dateTo} onChange={v => { setDateTo(v); setGenerated(false) }} />
          </div>
          {showAccountSelector ? (
            <div>
              <label htmlFor="reportstab-cuenta-libro-mayor" className="block text-xs font-medium text-content-muted mb-1">
                Cuenta {accountRequired ? '*' : '(opcional — todas)'}
              </label>
              <Autocomplete id="reportstab-cuenta-libro-mayor"
                items={accountItems}
                value={selectedAccountId}
                displayValue={selectedAccountLabel}
                onChange={() => { /* selección vía onSelect */ }}
                onSelect={(item: AutocompleteItem) => { setSelectedAccountId(item.id); setGenerated(false) }}
                placeholder={accountRequired ? 'Buscar cuenta…' : 'Todas las cuentas con movimientos'}
              />
              {selectedAccountId && !accountRequired && (
                <button
                  type="button"
                  onClick={() => { setSelectedAccountId(''); setGenerated(false) }}
                  className="mt-1 text-xs text-content-muted hover:text-content underline"
                >
                  Limpiar (volver a todas)
                </button>
              )}
            </div>
          ) : (
            <div />
          )}
        </div>
        {(reportType === 'analysis' || reportType === 'income-statement') && (
          <div className="mt-3">
            <label htmlFor="reportstab-centro-de-costo-opcional" className="block text-xs font-medium text-content-muted mb-1">Centro de Costo (opcional)</label>
            <div className="w-64">
              <Autocomplete id="reportstab-centro-de-costo-opcional"
                items={costCenterItems}
                value={selectedCostCenterId}
                displayValue={selectedCostCenterLabel}
                onChange={() => { /* selección vía onSelect */ }}
                onSelect={(item: AutocompleteItem) => { setSelectedCostCenterId(item.id); setGenerated(false) }}
                placeholder="Todos los centros de costo"
              />
              {selectedCostCenterId && (
                <button
                  type="button"
                  onClick={() => { setSelectedCostCenterId(''); setGenerated(false) }}
                  className="mt-1 text-xs text-content-muted hover:text-content underline"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={
              !dateFrom || !dateTo ||
              (accountRequired && !selectedAccountId)
            }
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-50"
          >
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
            Generar Reporte
          </button>
        </div>
      </div>

      {/* Report results */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Generando...
        </div>
      )}

      {/* Libro Diario */}
      {!isLoading && journal.data && reportType === 'journal' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-content">
              Libro Diario — {journal.data.date_from} al {journal.data.date_to}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-content-subtle">{journal.data.count} comprobante{journal.data.count !== 1 ? 's' : ''}</span>
              <ExportButton onClick={() => exportJournalExcel(journal.data!, getPdfCompany())} disabled={!journal.data.count} />
              <PDFButton
                onClick={() => withLogo(logo => exportJournalPDF(journal.data!, getPdfCompany(), logo))}
                disabled={!journal.data.count}
              />
            </div>
          </div>
          {journal.data.count === 0 ? (
            <EmptyState title="No se encontraron movimientos aprobados en el período." size="sm" />
          ) : (
            <div className="space-y-3">
              {journal.data.entries.map(entry => (
                <div key={entry.id} className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
                  <button
                    type="button"
                    onClick={() => openVoucher(entry.id)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-row-alt/50 border-b border-divider text-left hover:bg-soft transition-colors"
                    title="Abrir comprobante"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-semibold text-primary underline decoration-dotted">{entry.number}</span>
                      <span className="text-xs text-content-muted">{fmtDate(entry.date, locale)}</span>
                      <span className="text-sm text-content">{entry.description}</span>
                      {entry.closing_kind && (
                        <span
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            entry.closing_kind === 'reopening'
                              ? 'bg-warning-surface border-warning-border text-warning-content-strong'
                              : 'bg-soft border-divider text-content-muted'
                          }`}
                          title={TIPO_DE_ASIENTO[entry.closing_kind].ayuda}
                        >
                          {TIPO_DE_ASIENTO[entry.closing_kind].rotulo}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-content-muted">{fmtCLP(entry.total_debe)}</span>
                  </button>
                  {/* ⚠️ EXENTA del barrido al DataTable, a propósito: no tiene <thead>.
                      Son las líneas de UN asiento dentro de su tarjeta del Libro
                      Diario, no una tabla de datos — migrarla le agregaría
                      encabezados de columna que hoy no tiene. Mismo criterio que
                      FirmAdminDetailModal y HrAssetsTab. */}
                  <table className="w-full">
                    <tbody>
                      {entry.lines.map((line, i) => (
                        <tr key={i} className="border-b border-divider last:border-0">
                          <td className="px-4 py-1.5 text-xs font-mono text-content-muted w-20">{line.account_code}</td>
                          <td className="px-2 py-1.5 text-xs text-content-muted">{line.account_name}</td>
                          <td className="px-2 py-1.5 text-xs text-content-subtle">{line.glosa}</td>
                          <td className="px-4 py-1.5 text-xs text-right font-medium text-content w-28">
                            {parseFloat(line.debe) > 0 ? fmtNum(line.debe) : ''}
                          </td>
                          <td className="px-4 py-1.5 text-xs text-right font-medium text-content-muted w-28">
                            {parseFloat(line.haber) > 0 ? fmtNum(line.haber) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Libro Mayor */}
      {!isLoading && ledger.data && reportType === 'ledger' && (() => {
        const d = ledger.data!
        const allLedgers: LedgerAccountData[] = isSingleLedger(d) ? [d] : d.accounts
        const totalCount = isSingleLedger(d) ? d.count : d.account_count
        const titleSuffix = isSingleLedger(d)
          ? `${d.account_code} ${d.account_name}`
          : `Todas las cuentas (${d.account_count})`

        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">
                Libro Mayor — {titleSuffix}
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-content-subtle">
                  {isSingleLedger(d)
                    ? `${d.count} movimiento${d.count !== 1 ? 's' : ''}`
                    : `${d.account_count} cuenta${d.account_count !== 1 ? 's' : ''}`
                  }
                </span>
                <ExportButton
                  onClick={() => exportLedgerExcel(d.date_from, d.date_to, allLedgers, getPdfCompany())}
                  disabled={totalCount === 0}
                />
                <PDFButton
                  onClick={() => withLogo(logo => exportLedgerPDF(d.date_from, d.date_to, allLedgers, getPdfCompany(), logo))}
                  disabled={totalCount === 0}
                />
              </div>
            </div>
            {totalCount === 0 ? (
              <EmptyState title="No hay movimientos en el período." size="sm" />
            ) : (
              allLedgers.map(acct => (
                <LedgerTable key={acct.account_code} data={acct} onVoucherClick={openVoucher} />
              ))
            )}
          </div>
        )
      })()}

      {/* Análisis de Cuenta */}
      {!isLoading && analysis.data && reportType === 'analysis' && (
        <div>
          {drillDownFrom && (
            <button
              type="button"
              onClick={backToPreviousReport}
              className="mb-3 inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover underline decoration-dotted"
            >
              ← Volver al reporte anterior
            </button>
          )}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-content">
              Análisis — {analysis.data.account_code} {analysis.data.account_name}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-content-subtle">{analysis.data.count} movimiento{analysis.data.count !== 1 ? 's' : ''}</span>
              <ExportButton onClick={() => exportAnalysisExcel(analysis.data!, getPdfCompany())} disabled={!analysis.data.count} />
            </div>
          </div>
          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
            <div className="px-4 py-2.5 bg-row-alt/50 border-b border-divider flex items-center gap-6 text-xs text-content-muted">
              <span><span className="font-medium">Período:</span> {analysis.data.date_from} al {analysis.data.date_to}</span>
              <span><span className="font-medium">Saldo inicial:</span> {fmtNum(analysis.data.opening_balance)}</span>
            </div>
            {analysis.data.count === 0 ? (
              <EmptyState title="No hay movimientos en el período para esta cuenta." size="sm" />
            ) : (
              <DataTable
                columns={[
                  { key: 'date', header: 'Fecha', render: m => fmtDate(m.date, locale) },
                  {
                    key: 'voucher_number', header: 'Comprobante',
                    render: m => <span className="font-mono text-primary underline decoration-dotted">{m.voucher_number}</span>,
                  },
                  { key: 'glosa', header: 'Glosa' },
                  {
                    key: 'cost_center_code', header: 'Centro de Costo',
                    render: m => (m.cost_center_code ? `${m.cost_center_code} ${m.cost_center_name}` : '—'),
                  },
                  { key: 'debe', header: 'Debe', numeric: true, sortValue: m => parseFloat(m.debe), render: m => (parseFloat(m.debe) > 0 ? fmtNum(m.debe) : '—') },
                  { key: 'haber', header: 'Haber', numeric: true, sortValue: m => parseFloat(m.haber), render: m => (parseFloat(m.haber) > 0 ? fmtNum(m.haber) : '—') },
                  {
                    key: 'running_balance', header: 'Saldo', numeric: true,
                    sortValue: m => parseFloat(m.running_balance),
                    render: m => <span className="font-semibold text-content">{fmtNum(m.running_balance)}</span>,
                  },
                ]}
                rows={analysis.data.movements}
                rowKey={(_, i) => i}
                onRowClick={m => openVoucher(m.voucher_id)}
                surface={false}
                minWidth={960}
                caption="Movimientos del análisis de cuenta"
              />
            )}
          </div>
        </div>
      )}

      {/* Balance de Comprobación */}
      {!isLoading && trialBalance.data && reportType === 'trial-balance' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-content">
              Balance de Comprobación — {trialBalance.data.date_from} al {trialBalance.data.date_to}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-content-subtle">{trialBalance.data.count} cuenta{trialBalance.data.count !== 1 ? 's' : ''}</span>
              <ExportButton onClick={() => exportTrialBalanceExcel(trialBalance.data!, getPdfCompany())} disabled={!trialBalance.data.count} />
            </div>
          </div>
          {trialBalance.data.count === 0 ? (
            <EmptyState title="No hay cuentas con movimientos en el período." size="sm" />
          ) : (
            <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
              {/* ⚠️ EXENTA del barrido al DataTable, a propósito: su encabezado
                  tiene DOS niveles (rowSpan/colSpan) — Saldo Inicial, Movimientos
                  y Saldo Final, cada uno con Debe y Haber. El DataTable emite una
                  sola fila de <th>, así que soportarlo sería una capacidad nueva
                  del componente. Medido: es la ÚNICA tabla así en todo el
                  producto, o sea no justifica construirla. */}
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-divider bg-row-alt/50">
                    <th className="px-3 py-3 text-left font-medium text-content-muted" rowSpan={2}>Código</th>
                    <th className="px-3 py-3 text-left font-medium text-content-muted" rowSpan={2}>Cuenta</th>
                    <th className="px-3 py-3 text-center font-medium text-content-muted" colSpan={2}>Saldo Inicial</th>
                    <th className="px-3 py-3 text-center font-medium text-content-muted" colSpan={2}>Movimientos</th>
                    <th className="px-3 py-3 text-center font-medium text-content-muted" colSpan={2}>Saldo Final</th>
                  </tr>
                  <tr className="border-b border-divider bg-row-alt/50">
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Debe</th>
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Haber</th>
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Debe</th>
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Haber</th>
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Debe</th>
                    <th className="px-3 py-2 text-right font-medium text-content-muted">Haber</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {trialBalance.data.accounts.map(row => {
                    const drillable = accountByCode.has(row.account_code)
                    return (
                    <tr
                      key={row.account_code}
                      className={`hover:bg-soft ${drillable ? 'cursor-pointer' : ''}`}
                      onClick={drillable ? () => drillDownByCode(row.account_code) : undefined}
                      title={drillable ? 'Ver movimientos de esta cuenta' : undefined}
                    >
                      <td className={`px-3 py-2 font-mono ${drillable ? 'text-primary underline decoration-dotted' : 'text-content'}`}>{row.account_code}</td>
                      <td className="px-3 py-2 text-content-muted">
                        <span className="text-xs text-content-subtle mr-1">
                          {ACCOUNT_TYPE_LABELS[row.account_type]}
                        </span>
                        {row.account_name}
                      </td>
                      <td className="px-3 py-2 text-right text-content">{fmtNum(row.opening_debe)}</td>
                      <td className="px-3 py-2 text-right text-content-muted">{fmtNum(row.opening_haber)}</td>
                      <td className="px-3 py-2 text-right text-content">{fmtNum(row.period_debe)}</td>
                      <td className="px-3 py-2 text-right text-content-muted">{fmtNum(row.period_haber)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-content">{fmtNum(row.final_debe)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-content-muted">{fmtNum(row.final_haber)}</td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Balance de 8 Columnas */}
      {!isLoading && eightCol.data && reportType === 'eight-column-balance' && (() => {
        const COL8 = [
          { key: 'debitos', label: 'Débitos' }, { key: 'creditos', label: 'Créditos' },
          { key: 'deudor', label: 'Deudor' }, { key: 'acreedor', label: 'Acreedor' },
          { key: 'activo', label: 'Activo' }, { key: 'pasivo', label: 'Pasivo' },
          { key: 'perdida', label: 'Pérdida' }, { key: 'ganancia', label: 'Ganancia' },
        ] as const
        const d = eightCol.data!
        const visibleAccounts = hideZeroAccounts
          ? d.accounts.filter(row =>
              parseFloat(row.deudor) !== 0 || parseFloat(row.acreedor) !== 0
            )
          : d.accounts
        // Recalcula totales según las cuentas visibles para que el filtro
        // "Ocultar cuentas sin saldo" sea coherente con la fila TOTALES.
        const visibleTotals = visibleAccounts.reduce(
          (acc, row) => {
            for (const k of ['debitos', 'creditos', 'deudor', 'acreedor', 'activo', 'pasivo', 'perdida', 'ganancia'] as const) {
              acc[k] = (parseFloat(acc[k]) + parseFloat(row[k] || '0')).toString()
            }
            return acc
          },
          { debitos: '0', creditos: '0', deudor: '0', acreedor: '0', activo: '0', pasivo: '0', perdida: '0', ganancia: '0' } as Record<string, string>,
        )
        const visibleResultado = (parseFloat(visibleTotals.ganancia) - parseFloat(visibleTotals.perdida)).toString()
        const resEj = parseFloat(visibleResultado)
        const resEjIsLoss = resEj < 0
        const handleExport8Col = () => {
          const company = getPdfCompany()
          const colHeaders = ['Cuenta', 'Nombre', 'Débitos', 'Créditos', 'Deudor', 'Acreedor', 'Activo', 'Pasivo', 'Pérdida', 'Ganancia']

          // Construir AOA: primero encabezado empresa (6 filas), luego fila de cabeceras, luego datos
          const aoa: (string | number)[][] = [
            [company.legal_name || ''],
            [company.tax_id ? `RUT: ${company.tax_id}` : ''],
            [company.business_activity || ''],
            ['Balance de 8 Columnas'],
            [`Período: ${d.date_from} al ${d.date_to}`],
            [], // fila en blanco separadora
            colHeaders,
            ...visibleAccounts.map((r: EightColumnBalanceRow) => [
              r.account_code, r.account_name,
              parseFloat(r.debitos) || 0, parseFloat(r.creditos) || 0,
              parseFloat(r.deudor) || 0, parseFloat(r.acreedor) || 0,
              parseFloat(r.activo) || 0, parseFloat(r.pasivo) || 0,
              parseFloat(r.perdida) || 0, parseFloat(r.ganancia) || 0,
            ]),
          ]
          aoa.push([
            '', 'TOTALES',
            parseFloat(visibleTotals.debitos) || 0, parseFloat(visibleTotals.creditos) || 0,
            parseFloat(visibleTotals.deudor) || 0, parseFloat(visibleTotals.acreedor) || 0,
            parseFloat(visibleTotals.activo) || 0, parseFloat(visibleTotals.pasivo) || 0,
            parseFloat(visibleTotals.perdida) || 0, parseFloat(visibleTotals.ganancia) || 0,
          ])

          const ws = XLSX.utils.aoa_to_sheet(aoa)

          // Anchos de columna: Cuenta=12, Nombre=42, 8 columnas numéricas=15 c/u
          ws['!cols'] = [
            { wch: 12 }, { wch: 42 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
          ]

          // Bold en fila de cabeceras (fila 7, índice 6) — compatible con xlsx 0.18.x
          const headerRowIdx = 6
          colHeaders.forEach((_, ci) => {
            const cellAddr = XLSX.utils.encode_cell({ r: headerRowIdx, c: ci })
            if (ws[cellAddr]) ws[cellAddr].s = { font: { bold: true } }
          })

          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Balance 8 Col')
          XLSX.writeFile(wb, `balance_8_columnas_${d.date_from}_${d.date_to}.xlsx`, { cellStyles: true })
        }
        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">
                Balance de 8 Columnas — {d.date_from} al {d.date_to}
              </h3>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hideZeroAccounts}
                    onChange={e => setHideZeroAccounts(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-divider-strong text-primary focus:ring-primary"
                  />
                  Ocultar cuentas sin saldo
                </label>
                <span className="text-xs text-content-subtle">{visibleAccounts.length} cuenta{visibleAccounts.length !== 1 ? 's' : ''}</span>
                <ExportButton onClick={handleExport8Col} disabled={!d.accounts?.length} />
                <PDFButton
                  onClick={() => withLogo(logo => exportEightColPDF({ ...d, accounts: visibleAccounts, totals: visibleTotals as EightColumnBalanceResponse['totals'], resultado_ejercicio: visibleResultado }, getPdfCompany(), logo))}
                  disabled={!d.accounts?.length}
                />
              </div>
            </div>
            {d.count === 0 ? (
              <EmptyState title="No hay cuentas con movimientos en el período." size="sm" />
            ) : (
              <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-divider bg-row-alt/50">
                      <th className="px-3 py-3 text-left font-medium text-content-muted sticky left-0 bg-row-alt/50">Cuenta</th>
                      {COL8.map(h => (
                        <th key={h.key} className="px-3 py-3 text-right font-medium text-content-muted whitespace-nowrap">{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {visibleAccounts.map(row => {
                      const drillable = accountByCode.has(row.account_code)
                      return (
                      <tr
                        key={row.account_code}
                        className={`group hover:bg-soft ${drillable ? 'cursor-pointer' : ''}`}
                        onClick={drillable ? () => drillDownByCode(row.account_code) : undefined}
                        title={drillable ? 'Ver movimientos de esta cuenta' : undefined}
                      >
                        <td className="px-3 py-2 sticky left-0 bg-surface group-hover:bg-soft transition-colors">
                          <span className={`font-mono mr-1 ${drillable ? 'text-primary underline decoration-dotted' : 'text-content-muted'}`}>{row.account_code}</span>
                          <span className="font-medium text-content">{row.account_name}</span>
                        </td>
                        {COL8.map(h => {
                          const val = row[h.key as keyof EightColumnBalanceRow] as string
                          return (
                            <td key={h.key} className="px-3 py-2 text-right tabular-nums">
                              {parseFloat(val) !== 0 ? fmtCLP(val) : <span className="text-content-subtle">—</span>}
                            </td>
                          )
                        })}
                      </tr>
                      )
                    })}
                    {/* Totales — recalculados sobre las cuentas visibles */}
                    <tr className="border-t-2 border-divider-strong bg-surface-muted font-semibold">
                      <td className="px-3 py-2.5 text-content sticky left-0 bg-surface-muted">TOTALES</td>
                      {COL8.map(h => (
                        <td key={h.key} className="px-3 py-2.5 text-right tabular-nums text-content">
                          {fmtCLP(visibleTotals[h.key])}
                        </td>
                      ))}
                    </tr>
                    {/* Resultado del ejercicio — derivado de las cuentas visibles */}
                    {visibleResultado !== '0' && (
                      <tr className={resEjIsLoss ? 'bg-danger-surface' : 'bg-success-surface'}>
                        <td className={`px-3 py-2.5 font-semibold sticky left-0 ${resEjIsLoss ? 'text-danger-content bg-danger-surface' : 'text-success-content bg-success-surface'}`}>
                          Resultado del Ejercicio
                          <span className={`ml-1.5 text-xs font-medium px-1.5 py-0.5 rounded ${resEjIsLoss ? 'bg-danger-surface-strong text-danger-content' : 'bg-success-surface-strong text-success-content'}`}>
                            {resEjIsLoss ? 'Pérdida' : 'Ganancia'}
                          </span>
                        </td>
                        {COL8.map(h => {
                          const showHere = resEjIsLoss ? h.key === 'perdida' : h.key === 'ganancia'
                          const displayVal = showHere ? String(Math.abs(resEj)) : ''
                          return (
                            <td key={h.key} className={`px-3 py-2.5 text-right tabular-nums font-semibold ${resEjIsLoss ? 'text-danger-content' : 'text-success-content'}`}>
                              {showHere ? fmtCLP(displayVal) : ''}
                            </td>
                          )
                        })}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })()}

      {/* Balance Clasificado */}
      {!isLoading && classifiedBalance.data && reportType === 'classified-balance' && (() => {
        const d = classifiedBalance.data!
        const hasData = d.current_assets.accounts.length > 0 || d.non_current_assets.accounts.length > 0
          || d.current_liabilities.accounts.length > 0 || d.non_current_liabilities.accounts.length > 0
          || d.equity.accounts.length > 0

        const BalanceSection = ({ title, accounts, total }: {
          title: string; accounts: { account_code: string; account_name: string; balance: string }[]; total: string
        }) => (
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">{title}</h4>
            {accounts.map(a => (
              <div
                key={a.account_code}
                className={`flex justify-between py-0.5 px-2 text-xs hover:bg-soft ${accountByCode.has(a.account_code) ? 'cursor-pointer' : ''}`}
                onClick={accountByCode.has(a.account_code) ? () => drillDownByCode(a.account_code) : undefined}
                title={accountByCode.has(a.account_code) ? 'Ver movimientos de esta cuenta' : undefined}
              >
                <span><span className={`font-mono mr-1.5 ${accountByCode.has(a.account_code) ? 'text-primary underline decoration-dotted' : 'text-content-subtle'}`}>{a.account_code}</span>{a.account_name}</span>
                <span className="tabular-nums font-medium">{fmtCLP(a.balance)}</span>
              </div>
            ))}
            <div className="flex justify-between py-1 px-2 text-xs font-semibold border-t border-divider mt-1">
              <span>Total {title}</span>
              <span className="tabular-nums">{fmtCLP(total)}</span>
            </div>
          </div>
        )

        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">
                Balance Clasificado — Al {d.date_to}
              </h3>
              <div className="flex items-center gap-2">
                <ExportButton onClick={() => exportClassifiedBalanceExcel(d, getPdfCompany())} disabled={!hasData} />
                <PDFButton
                  onClick={() => withLogo(logo => exportClassifiedBalancePDF(d, getPdfCompany(), logo))}
                  disabled={!hasData}
                />
              </div>
            </div>
            {!hasData ? (
              <EmptyState title="No hay cuentas con movimientos." size="sm" />
            ) : (
              <>
                {/* Company header */}
                {d.company.legal_name && (
                  <div className="bg-glass-3 rounded-xl border border-divider p-4 mb-4 text-center">
                    <p className="font-semibold text-content">{d.company.legal_name}</p>
                    {d.company.tax_id && <p className="text-xs text-content-muted">RUT: {formatRut(d.company.tax_id)}</p>}
                    {d.company.address && <p className="text-xs text-content-subtle">{d.company.address}</p>}
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left: Assets */}
                  <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
                    <h3 className="text-sm font-bold text-content mb-3 border-b border-divider pb-2">ACTIVOS</h3>
                    <BalanceSection title="Activos Corrientes" accounts={d.current_assets.accounts} total={d.current_assets.total} />
                    <BalanceSection title="Activos No Corrientes" accounts={d.non_current_assets.accounts} total={d.non_current_assets.total} />
                    <div className="flex justify-between py-2 px-2 text-sm font-bold border-t-2 border-divider-strong mt-2">
                      <span>TOTAL ACTIVOS</span>
                      <span className="tabular-nums text-primary-hover">{fmtCLP(d.total_assets)}</span>
                    </div>
                  </div>
                  {/* Right: Liabilities + Equity */}
                  <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
                    <h3 className="text-sm font-bold text-content mb-3 border-b border-divider pb-2">PASIVOS Y PATRIMONIO</h3>
                    <BalanceSection title="Pasivos Corrientes" accounts={d.current_liabilities.accounts} total={d.current_liabilities.total} />
                    <BalanceSection title="Pasivos No Corrientes" accounts={d.non_current_liabilities.accounts} total={d.non_current_liabilities.total} />
                    {/* Equity */}
                    <div className="mb-3">
                      <h4 className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">Patrimonio</h4>
                      {d.equity.accounts.map(a => (
                        <div key={a.account_code} className="flex justify-between py-0.5 px-2 text-xs hover:bg-soft">
                          <span><span className="font-mono text-content-subtle mr-1.5">{a.account_code}</span>{a.account_name}</span>
                          <span className="tabular-nums font-medium">{fmtCLP(a.balance)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between py-0.5 px-2 text-xs italic text-content-muted">
                        <span>Ganancia (pérdida) del ejercicio</span>
                        <span className="tabular-nums font-medium">{fmtCLP(d.equity.result_current_year)}</span>
                      </div>
                      <div className="flex justify-between py-1 px-2 text-xs font-semibold border-t border-divider mt-1">
                        <span>Total Patrimonio</span>
                        <span className="tabular-nums">{fmtCLP(d.equity.total)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between py-2 px-2 text-sm font-bold border-t-2 border-divider-strong mt-2">
                      <span>TOTAL PATRIMONIO Y PASIVOS</span>
                      <span className="tabular-nums text-primary-hover">{fmtCLP(d.total_liabilities_equity)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )
      })()}

      {/* Estado de Resultados */}
      {!isLoading && incomeStatement.data && reportType === 'income-statement' && (() => {
        const d = incomeStatement.data!
        const hasData = d.line_items.length > 0

        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">
                Estado de Resultados — {d.date_from} al {d.date_to}
              </h3>
              <div className="flex items-center gap-2">
                <ExportButton onClick={() => exportIncomeStatementExcel(d, getPdfCompany())} disabled={!hasData} />
                <PDFButton
                  onClick={() => withLogo(logo => exportIncomeStatementPDF(d, getPdfCompany(), logo))}
                  disabled={!hasData}
                />
              </div>
            </div>
            {!hasData ? (
              <EmptyState title="No hay movimientos en el período." size="sm" />
            ) : (
              <>
                {d.company.legal_name && (
                  <div className="bg-glass-3 rounded-xl border border-divider p-4 mb-4 text-center">
                    <p className="font-semibold text-content">{d.company.legal_name}</p>
                    {d.company.tax_id && <p className="text-xs text-content-muted">RUT: {formatRut(d.company.tax_id)}</p>}
                    {d.company.address && <p className="text-xs text-content-subtle">{d.company.address}</p>}
                  </div>
                )}
                <DataTable
                  columns={[
                    {
                      key: 'label', header: 'Concepto',
                      render: li => {
                        const drillable = !li.is_subtotal && !!li.account_code && accountByCode.has(li.account_code)
                        return (
                          <span
                            className={li.is_subtotal ? 'font-semibold text-content' : 'text-content-muted'}
                            style={{ paddingLeft: `${li.indent * 1.5}rem` }}
                          >
                            {li.account_code && (
                              <span className={`font-mono text-xs mr-1.5 ${drillable ? 'text-primary underline decoration-dotted' : 'text-content-subtle'}`}>
                                {li.account_code}
                              </span>
                            )}
                            {li.label}
                          </span>
                        )
                      },
                    },
                    {
                      key: 'amount', header: 'Monto', numeric: true, width: 160,
                      sortValue: li => parseFloat(li.amount),
                      render: li => {
                        const n = parseFloat(li.amount)
                        const formatted = n < 0 ? `(${formatMoneyForTenant(Math.abs(n))})` : fmtCLP(li.amount)
                        return (
                          <span className={`${li.is_subtotal ? 'font-semibold text-content' : 'text-content'} ${n < 0 ? 'text-danger-content-soft' : ''}`}>
                            {formatted}
                          </span>
                        )
                      },
                    },
                  ]}
                  rows={d.line_items}
                  rowKey={(_, i) => i}
                  rowClassName={li => (li.is_subtotal ? 'bg-surface-muted border-t border-divider' : undefined)}
                  onRowClick={li => {
                    // Solo las cuentas hoja con movimientos abren el detalle.
                    if (!li.is_subtotal && li.account_code && accountByCode.has(li.account_code)) {
                      drillDownByCode(li.account_code)
                    }
                  }}
                  minWidth={420}
                  caption="Estado de resultados"
                />
              </>
            )}
          </div>
        )
      })()}

      {!isLoading && cashFlow.data && reportType === 'cash-flow' && (() => {
        const d = cashFlow.data!
        const items = cashFlowLineItems(d)
        const hasData = items.length > 2  // más que solo inicial + final
        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">
                Estado de Flujo de Efectivo — {d.date_from} al {d.date_to}
              </h3>
              <div className="flex items-center gap-2">
                <ExportButton onClick={() => exportCashFlowExcel(d, getPdfCompany())} disabled={!hasData} />
                <PDFButton
                  onClick={() => withLogo(logo => exportCashFlowPDF(d, getPdfCompany(), logo))}
                  disabled={!hasData}
                />
              </div>
            </div>

            {d.warnings.length > 0 && (
              <div className="mb-4 rounded-xl border border-warning-border-strong bg-warning-surface p-3">
                <p className="text-xs font-semibold text-warning-content-strong mb-1">Advertencias</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {d.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-warning-content-strong">{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {d.company.legal_name && (
              <div className="bg-glass-3 rounded-xl border border-divider p-4 mb-4 text-center">
                <p className="font-semibold text-content">{d.company.legal_name}</p>
                {d.company.tax_id && <p className="text-xs text-content-muted">RUT: {formatRut(d.company.tax_id)}</p>}
                {d.company.address && <p className="text-xs text-content-subtle">{d.company.address}</p>}
              </div>
            )}

            <DataTable
              columns={[
                {
                  key: 'label', header: 'Concepto',
                  render: li => (
                    <span
                      className={li.isSubtotal ? 'font-semibold text-content' : 'text-content-muted'}
                      // La sangría marca el nivel del concepto en el estado.
                      style={{ paddingLeft: `${li.indent * 1.5}rem` }}
                    >
                      {li.label}
                    </span>
                  ),
                },
                {
                  key: 'amount', header: 'Monto', numeric: true, width: 160,
                  sortValue: li => parseFloat(li.amount),
                  render: li => {
                    const n = parseFloat(li.amount)
                    // Los negativos van entre paréntesis, que es la convención contable.
                    const formatted = n < 0 ? `(${formatMoneyForTenant(Math.abs(n))})` : fmtCLP(li.amount)
                    return (
                      <span className={`${li.isSubtotal ? 'font-semibold text-content' : 'text-content'} ${n < 0 ? 'text-danger-content-soft' : ''}`}>
                        {formatted}
                      </span>
                    )
                  },
                },
              ]}
              rows={items}
              rowKey={(_, i) => i}
              // Las filas de subtotal se destacan ENTERAS.
              rowClassName={li => (li.isSubtotal ? 'bg-surface-muted border-t border-divider' : undefined)}
              minWidth={420}
              caption="Estado de flujo de efectivo"
            />

            <div className={`mt-3 text-xs px-4 py-2 rounded-lg ${d.cuadre.cuadra
              ? 'bg-success-surface text-success-content'
              : 'bg-warning-surface text-warning-content-strong'}`}>
              {d.cuadre.cuadra
                ? 'Cuadre correcto: la suma de las actividades explica la variación del efectivo.'
                : `Descuadre de ${fmtCLP(d.cuadre.diferencia)} entre la variación de saldos (${fmtCLP(d.cuadre.variacion_saldos)}) y la suma de flujos (${fmtCLP(d.cuadre.suma_flujos)}); suele deberse a asientos de apertura/cierre en el período.`}
            </div>
          </div>
        )
      })()}

      {viewVoucherId && (
        <VoucherViewerModal
          voucherId={viewVoucherId}
          onClose={() => setViewVoucherId(null)}
        />
      )}
    </div>
  )
}
